import styles from "../../../styles/pages/Employees/EmpoyeeDesk.module.css";
import React, { useState, useEffect, useContext } from 'react';
import { getAllDesksApi } from "../../../utils/api/Admin/desk/getAllDesksApi";
import { AddDeskModal } from "../../../components/Modal/Employees/AddDeskModal";
import { EditDeskModal } from "../../../components/Modal/Employees/EditDeskModal";
import { LoadingCircle } from "../../../components/UI/Custom/LoadingCircle";
import { LangContext } from "../../../context/LangContext";
import { ThemeContext } from "../../../context/ThemeContext";

const translations = {
    ru: {
        accessDenied: "У вас нет разрешения на просмотр десков.",
        addDesk: "Добавить Деск",
        error: "Ошибка: ",
        tableHeaders: {
            id: "ID",
            name: "НАЗВАНИЕ",
            description: "ОПИСАНИЕ",
        },
        previous: "Предыдущая",
        next: "Следующая",
        pageInfo: (currentPage, totalPages) => `Страница ${currentPage} из ${totalPages}`,
    },
    en: {
        accessDenied: "You do not have permission to view desks.",
        addDesk: "Add Desk",
        error: "Error: ",
        tableHeaders: {
            id: "ID",
            name: "NAME",
            description: "DESCRIPTION",
        },
        previous: "Previous",
        next: "Next",
        pageInfo: (currentPage, totalPages) => `Page ${currentPage} of ${totalPages}`,
    },
};

export const EmpoyeeDesk = ({ permissions }) => {
    const { langSelected } = useContext(LangContext);
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';
    const t = translations[langSelected] || translations.ru;

    const [desks, setDesks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [perPage, setPerPage] = useState(15);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingDeskId, setEditingDeskId] = useState(null);

    const loadDesks = async (page = currentPage) => {
        setLoading(true);
        setError(null);
        try {
            const result = await getAllDesksApi(page, perPage);
            const { data, meta } = result;
            setDesks(data);
            setTotalPages(meta.last_page);
            setCurrentPage(meta.current_page);
        } catch (err) {
            setError(err.message || 'Не удалось загрузить список десков.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (permissions['user.list_desks']) {
            loadDesks();
        } else {
            setLoading(false);
            setError(t.accessDenied);
        }
    }, [currentPage, perPage, permissions, t.accessDenied]);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const openAddDeskModal = () => {
        setIsAddModalOpen(true);
    };

    const closeAddDeskModal = () => {
        setIsAddModalOpen(false);
    };

    const handleAddDesk = async () => {
        closeAddDeskModal();
        await loadDesks(1);
    };

    const openEditDeskModal = (id) => {
        setEditingDeskId(id);
        setIsEditModalOpen(true);
    };

    const closeEditDeskModal = () => {
        setIsEditModalOpen(false);
        setEditingDeskId(null);
    };

    const handleEditDesk = async () => {
        closeEditDeskModal();
        await loadDesks(1);
    };

    return (
        <div className={`${styles.cardContainer} ${isDark ? styles.darkCardContainer : ''}`}>
            <div className={styles.cardHeader}>
                {permissions['user.create_desks'] && (
                    <button className={`${styles.addButton} ${isDark ? styles.darkAddButton : ''}`} onClick={openAddDeskModal}>
                        {t.addDesk}
                    </button>
                )}
            </div>

            {loading ? (
                <div className={styles.loadingOverlay}>
                    <LoadingCircle size={40} thickness={3} isDark={isDark} />
                </div>
            ) : error ? (
                <div className={`${styles.errorText} ${isDark ? styles.darkErrorText : ''}`}>{t.error}{error}</div>
            ) : (
                <div className={styles.tableWrapper}>
                    <table className={styles.table}>
                        <thead className={`${styles.tableHeader} ${isDark ? styles.darkTableHeader : ''}`}>
                            <tr>
                                <th>{t.tableHeaders.id}</th>
                                <th>{t.tableHeaders.name}</th>
                                <th>{t.tableHeaders.description}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {desks && desks.map((desk) => (
                                <tr key={desk.id} className={`${styles.tableRow} ${isDark ? styles.darkTableRow : ''}`}>
                                    <td className={`${styles.tableCell} ${isDark ? styles.darkTableCell : ''}`}>{desk.id}</td>
                                    <td
                                        className={`${styles.tableCell} ${isDark ? styles.darkTableCell : ''} ${permissions['user.update_desks'] ? styles.clickableName : ''}`}
                                        onClick={permissions['user.update_desks'] ? () => openEditDeskModal(desk.id) : undefined}
                                    >
                                        {desk.name}
                                    </td>
                                    <td className={`${styles.tableCell} ${isDark ? styles.darkTableCell : ''}`}>{desk.description}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {totalPages > 1 && (
                <div className={`${styles.pagination} ${isDark ? styles.darkPagination : ''}`}>
                    <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className={`${styles.paginationButton} ${isDark ? styles.darkPaginationButton : ''}`}
                    >
                        {t.previous}
                    </button>
                    <span className={`${styles.paginationInfo} ${isDark ? styles.darkPaginationInfo : ''}`}>{t.pageInfo(currentPage, totalPages)}</span>
                    <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className={`${styles.paginationButton} ${isDark ? styles.darkPaginationButton : ''}`}
                    >
                        {t.next}
                    </button>
                </div>
            )}

            {isAddModalOpen && (
                <AddDeskModal handleAddDeskModal={closeAddDeskModal} fetchDesks={handleAddDesk} />
            )}

            {isEditModalOpen && (
                <EditDeskModal
                    handleEditDeskModal={closeEditDeskModal}
                    fetchDesks={handleEditDesk}
                    deskId={editingDeskId}
                />
            )}
        </div>
    );
};